import deviceApi from '@api/deviceApi';
import { ActionButton } from '@components/actionButton';
import DefaultPage from '@components/defaultPage';
import { LoadingIndicator } from '@components/loadingIndicator';
import { RequireClaim } from '@components/requireClaim';
import Scanner from '@components/scanner';
import { useTranslator } from '@i18n';
import { Alert, Button } from '@mantine/core';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { handleError, useHandleError } from '@util/errors';
import parseCode from '@util/parseCode';
import { useState } from 'react';

export const Route = createFileRoute('/inventory/$deviceType/$deviceId/replace')({
  component: () => {
    const { deviceType, deviceId } = Route.useParams();
    const { locationId = '' } = Route.useSearch();

    return (
      <RequireClaim
        claim="devices/write"
        scope={locationId}
        fallback={<Navigate to="/inventory/$deviceType/$deviceId" params={{ deviceType, deviceId }} replace />}
      >
        <ReplaceDevicePage />
      </RequireClaim>
    );
  },
});

function ReplaceDevicePage() {
  const t = useTranslator();
  const { locationId = '' } = Route.useSearch();
  const { deviceType, deviceId } = Route.useParams();
  const [newDeviceId, setNewDeviceId] = useState<string>();

  const [device, deviceError] = deviceApi.get(locationId, deviceId).useCache({ loadingBoundary: true });
  const [newDevice, newDeviceError] = deviceApi
    .getGlobal(deviceType, newDeviceId ?? '')
    .useCache({ disabled: !newDeviceId });
  useHandleError([deviceError, newDeviceError]);
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  async function replace() {
    if (!device || !newDeviceId) {
      return;
    }

    try {
      setProcessing(true);

      await deviceApi.replace(deviceType, locationId, deviceId, newDeviceId);
      void navigate({
        to: '/inventory/$deviceType/$deviceId',
        params: { deviceType, deviceId: newDeviceId },
      });
    } catch (error) {
      setProcessing(false);
      throw error;
    }
  }

  const isValid = newDeviceId !== deviceId && newDevice && newDevice.type === deviceType;

  if (processing) {
    return (
      <DefaultPage backButton={{ to: '/inventory' }}>
        <LoadingIndicator size="grow" label={t('device.replace.processing')} />
      </DefaultPage>
    );
  }

  return (
    <DefaultPage
      backButton={{ to: '..' }}
      title={[t('device.replace.title'), device ? String(device.location.sequenceNumber).padStart(4, '0') : ''].join(
        ' ',
      )}
      css={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-md)',
      }}
    >
      {device === undefined ? (
        <LoadingIndicator size="grow" label={t('device.lookingUp')} css={{ justifySelf: 'start' }} />
      ) : device === null ? (
        t('device.replace.notFound', { id: deviceId })
      ) : (
        <>
          {!newDeviceId ? (
            <>
              <Scanner
                onScan={(text) => {
                  const result = parseCode(text);

                  if (result.type === 'device') {
                    setNewDeviceId(result.uid);
                  } else {
                    handleError(`${t('scanner.unknownCode')}: ${text}`);
                  }
                }}
                css={{
                  flex: 1,
                  margin: 'calc(0px - var(--mantine-spacing-md))',
                  justifySelf: 'center',
                  background: 'black',
                }}
              />

              <p css={{ textAlign: 'center' }}>{t('device.replace.instructions')}</p>
            </>
          ) : (
            <>
              <div>
                <div>
                  <b>{t('device.properties.description.label')}:</b> {device.location?.description}
                </div>

                <div>
                  <b>{t('device.replace.oldDevice')}:</b> {device.id}
                </div>

                <div>
                  <b>{t('device.replace.newDevice')}:</b> {newDeviceId}
                </div>
              </div>

              {newDeviceId === device.id ? (
                <Alert color="red" title={t('device.replace.sameDevice')} />
              ) : newDevice === undefined ? (
                <LoadingIndicator label={t('device.lookingUp')} />
              ) : newDevice === null ? (
                <Alert color="red" title={t('device.notFound', { id: deviceId })} />
              ) : newDevice.type !== device.type ? (
                <Alert color="red" title={t('device.replace.wrongType')} />
              ) : newDevice.location ? (
                <Alert color="yellow" title={t('device.create.alreadyRegistered')} />
              ) : null}

              <div css={{ flex: 1 }} />

              {isValid && <p css={{ textAlign: 'center' }}>{t('device.replace.confirmation')}</p>}

              <Button
                variant="transparent"
                onClick={() => setNewDeviceId(undefined)}
                disabled={!newDevice || newDevice.type !== device.type}
              >
                {t('device.replace.rescan')}
              </Button>

              <ActionButton disabled={!isValid} onClick={replace}>
                {t('device.replace.replace')}
              </ActionButton>
            </>
          )}
        </>
      )}
    </DefaultPage>
  );
}
